import type { CompanyEmployeeReviews } from "@shaarp/common";

/**
 * Coresignal's employee reviews (Glassdoor, Indeed…) as the widget shows
 * them: sub-scores out of 5, shares of reviewers, and how many gave each star.
 */

/** The sub-scores out of 5, in display order. */
export const EMPLOYEE_REVIEW_SCORES = [
  "culture_values",
  "work_life_balance",
  "compensation_benefits",
  "career_opportunities",
  "senior_management",
  "diversity_inclusion",
] as const;

/** The shares of reviewers, between 0 and 1, in display order. */
export const EMPLOYEE_REVIEW_SHARES = [
  "recommend",
  "business_outlook",
  "ceo_approval",
] as const;

export type EmployeeReviewScoreKey = (typeof EMPLOYEE_REVIEW_SCORES)[number];
export type EmployeeReviewShareKey = (typeof EMPLOYEE_REVIEW_SHARES)[number];

export type EmployeeReviewFigures = {
  readonly scores: readonly {
    readonly key: EmployeeReviewScoreKey;
    readonly value: number;
  }[];
  readonly shares: readonly {
    readonly key: EmployeeReviewShareKey;
    /** In percent, 0 to 100. */
    readonly percent: number;
  }[];
  /** Five stars to one, each with its count; empty without a distribution. */
  readonly distribution: readonly {
    readonly stars: 1 | 2 | 3 | 4 | 5;
    readonly count: number;
  }[];
};

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * The sub-scores, shares and distribution the reviews give.
 *
 * @param reviews The company's employee reviews.
 * @returns What they give; each list empty when they give none of it.
 */
export function employeeReviewFigures(
  reviews: CompanyEmployeeReviews | null | undefined,
): EmployeeReviewFigures {
  const breakdown = reviews?.scoreBreakdown ?? {};
  const distribution = reviews?.scoreDistribution ?? {};
  const counts = ([5, 4, 3, 2, 1] as const).map((stars) => ({
    stars,
    count: distribution[`score_${stars}`],
  }));
  return {
    scores: EMPLOYEE_REVIEW_SCORES.flatMap((key) => {
      const value = breakdown[key];
      return isNumber(value) && value > 0 ? [{ key, value }] : [];
    }),
    shares: EMPLOYEE_REVIEW_SHARES.flatMap((key) => {
      const value = breakdown[key];
      return isNumber(value) && value >= 0 && value <= 1
        ? [{ key, percent: Math.round(value * 100) }]
        : [];
    }),
    distribution: counts.some(({ count }) => isNumber(count) && count > 0)
      ? counts.map(({ stars, count }) => ({
          stars,
          count: isNumber(count) ? count : 0,
        }))
      : [],
  };
}
